import { useCallback, useState } from 'react'

import { useStore } from '@tanstack/react-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { BankSourceCreateResponse } from 'commons/types/DTO/bank/salesDataSourceWebPage'
import { Request } from 'commons/utils/request'
import { FormType, Toast, handleFormSubmissionError } from 'ui/components'

import { handlerServerError } from '@orbit/errors'

import { BankSourceSetupForm } from '../types'

export const useSaveSourceAndFilesManage = (
  sourceId: number | undefined,
  mrId: number | undefined,
  formApi: FormType<BankSourceSetupForm>,
  isCustomReport: boolean,
  returnUrl?: string
) => {
  const navigate = useNavigate()
  const [files, setFiles] = useState<File[]>([])
  const queryClient = useQueryClient()

  let sourceUpdated = false
  let filesUploadError = false

  const { mutate: uploadFile, isPending: isFileUploading } = useMutation<void, Error, FormData>({
    mutationFn: (opts) => Request.post(Request.urls.bank.salesDataSourceFileUpload, opts),
    onError: (error) => () => {
      filesUploadError = true
      return handlerServerError(error)
    }
  })

  const { mutate: deleteUploadedFile, isPending: isUploadedFileDeleting } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.salesDataSourceFile, { id }),
    onSuccess: async () => {
      Toast.successSmall('File was deleted.')
      if (sourceId) {
        queryClient.invalidateQueries({ queryKey: ['bank-source-setup-mode'] })
      }
    },
    onError: (error) => () => handlerServerError(error)
  })

  const { mutateAsync: updateBankSource, isPending: isSubmitting } = useMutation<
    BankSourceCreateResponse,
    Error,
    BankSourceSetupForm
  >({
    mutationFn: (opts) => Request.post(Request.urls.bank.salesDataBankSource, opts),
    onError: (error) => handleFormSubmissionError(error, formApi),
    onSuccess: async (response) => {
      sourceUpdated = true
      files.forEach(async (file) => {
        const form = new FormData()
        form.append('salesDataSourceId', response.id.toString())
        form.append('file', file)
        await uploadFile(form)
      })
    }
  })

  const checkErrorsAndRedirect = useCallback(() => {
    if (!isSubmitting && !isFileUploading && sourceUpdated) {
      if (files.length && filesUploadError) {
        Toast.errorSmall(`Banker source has been saved, but some files aren't uploaded.`)
      } else {
        Toast.successSmall('Banker source has been saved.')
      }
    }
    if (returnUrl) {
      navigate({ to: returnUrl })
    } else {
      if (mrId) {
        if (isCustomReport) {
          navigate({ to: routes.bank.hrr.details, params: { id: mrId.toString() } })
        } else {
          navigate({ to: '/mr/$id', params: { id: mrId.toString() } })
        }
      }
    }
  }, [files, isFileUploading, isSubmitting, sourceUpdated, filesUploadError, navigate, mrId, returnUrl])

  const addFiles = useCallback(
    (newFiles: File[]) => {
      setFiles([...files, ...newFiles])
    },
    [files]
  )

  const deleteFile = useCallback(
    (index: number) => {
      files.splice(index, 1)
      setFiles([...files])
    },
    [files]
  )

  const value = useStore(formApi.store, (s) => s.values)
  const onSubmit = useCallback(
    () => updateBankSource(value).then(() => setTimeout(() => checkErrorsAndRedirect(), 0)),
    [updateBankSource, value, checkErrorsAndRedirect]
  )

  return {
    files,
    addFiles,
    deleteFile,
    onSubmit,
    deleteUploadedFile,
    isFileUploading,
    isSubmitting,
    isUploadedFileDeleting
  }
}
